import { getFieldSortKey } from 'common/constants/ideas';

import type { Idea } from 'common/api/endpoints/idea';
import type { View } from 'common/api/endpoints/view';

type SortValue = string | number | Date | null;

const groupBySortKeyMap: Record<string, string> = {
  hierarchy: 'group',
  owner: 'owner',
  status: 'status',
};

/**
 * Sort ideas array to mimic the backend sorting logic.
 */
export function sortIdeas(ideas: Idea[], view: Pick<View, 'sort' | 'groupBy'>): Idea[] {
  const { sort, groupBy } = view;

  if (!sort) {
    return ideas;
  }

  const sortField = sort.field;
  const sortDirection = sort.direction === 'asc' ? 1 : -1;

  const groupBySortField = groupBy ? groupBySortKeyMap[groupBy] : null;
  const isSameField = groupBySortField === sortField;

  return ideas.sort((a, b) => {
    if (groupBy && !isSameField) {
      const groupByA = getGroupBySortValue(a, groupBy);
      const groupByB = getGroupBySortValue(b, groupBy);
      const groupByComparison = compareSortValues(groupByA, groupByB);

      if (groupByComparison !== 0) {
        return groupByComparison; // groupBy always uses ascending order
      }
    }

    const sortValueA = getSortValue(a, sortField);
    const sortValueB = getSortValue(b, sortField);
    const sortComparison = compareSortValues(sortValueA, sortValueB);

    if (sortComparison !== 0) {
      return sortComparison * sortDirection;
    }

    const idComparison = compareSortValues(a._id, b._id);
    return idComparison * sortDirection;
  });
}

/**
 * Get the sort value for a given field from an idea.
 */
function getSortValue(idea: Idea, fieldName: string): SortValue {
  if (fieldName === '_id') {
    return idea._id;
  }

  const value = idea.sortKeys[fieldName];
  if (value === null || value === undefined) {
    return null;
  }

  return getFieldSortKey(value) as SortValue;
}

/**
 * Get the groupBy sort value for an idea
 */
const getGroupBySortValue = (idea: Idea, groupBy: View['groupBy']): SortValue => {
  if (!groupBy) {
    return null;
  }

  const sortKeyField = groupBySortKeyMap[groupBy];
  if (!sortKeyField) {
    // Will throw if we add a new groupBy and forget to handle it here
    throw new Error(`Invalid groupBy: ${groupBy}`);
  }

  const value = idea.sortKeys[sortKeyField];
  if (value === null || value === undefined) {
    return null;
  }

  return getFieldSortKey(value) as SortValue;
};

/**
 * Compare two sort values, matching MongoDB's null ordering semantics:
 * - Null values are ordered before non-null values in ascending order
 * - Null values are ordered after non-null values in descending order
 */
export const compareSortValues = (a: SortValue, b: SortValue): number => {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return -1; // null < non-null (nulls first in ascending)
  }
  if (b === null) {
    return 1; // non-null > null (nulls first in ascending)
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, 'en', { sensitivity: 'base' });
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  return String(a).localeCompare(String(b), 'en', { sensitivity: 'base' });
};
